import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icon";

/**
 * Positioned context menu (mockup openMenu). `items`: { icon, label, run, danger, testId } or "-" (divider).
 * `top` is optional extra content above the items (e.g. a reactions row).
 * Clamped to the viewport; closes on outside click, Escape, scroll and resize.
 */
function Menu({ x, y, items, top = null, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos({
      left: Math.max(8, Math.min(x, window.innerWidth - r.width - 8)),
      top: Math.max(8, Math.min(y, window.innerHeight - r.height - 8)),
    });
  }, [x, y]);

  useEffect(() => {
    const away = (e) => { if (!ref.current?.contains(e.target)) onClose?.(); };
    const key = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    window.addEventListener("resize", onClose);
    window.addEventListener("scroll", onClose, true);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  return createPortal(
    <div className="menu" role="menu" ref={ref} style={pos}>
      {top}
      {items.map((it, i) =>
        it === "-" ? (
          <div key={i} className="divider" style={{ margin: "4px 0" }} />
        ) : (
          <button key={i} type="button" role="menuitem" data-testid={it.testId} className={it.danger ? "danger" : ""} onClick={() => { onClose?.(); it.run?.(); }}>
            {it.icon && <Icon name={it.icon} size={16} />}
            {it.label}
          </button>
        )
      )}
    </div>,
    document.body
  );
}

/**
 * const { menu, openMenu, closeMenu } = useContextMenu();
 * openMenu(x, y, items, top?)  -- then render {menu} anywhere in the component.
 */
export function useContextMenu() {
  const [state, setState] = useState(null);
  const closeMenu = useCallback(() => setState(null), []);
  const openMenu = useCallback((x, y, items, top = null) => setState({ x, y, items, top }), []);
  const menu = state ? <Menu {...state} onClose={closeMenu} /> : null;
  return { menu, openMenu, closeMenu };
}
